import { useMemo, useState } from 'react';
import { Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { laFechaHora } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { LIMITE_AUDITORIA, consultarAuditoria } from '../dominio/auditoriaApi';
import type { ActorTipoAuditoria, EntradaAuditoriaViajeReal } from '../dominio/auditoriaApi';
import { hayBackend } from '../config/entorno';

/**
 * Etiquetas del enum REAL `ActorTipo` del backend. Deliberadamente NO reusa `ETIQUETA_ROL` de
 * `sesion/permisos.ts`, que era lo que usaba la versión mock: ese diccionario está indexado por
 * `RolOperacion` (`operador`/`administrador`) — el rol de quien usa ESTA consola — y el backend
 * devuelve otra dimensión entera: QUÉ CLASE DE ACTOR provocó la transición, incluido el propio
 * sistema y el pasajero, que no son roles de consola. Mismo criterio que ya adoptaron
 * `Verificacion.tsx`, `Incidentes.tsx` y `MapaOperacion.tsx` con sus enums reales; mismas clases
 * CSS, para que se vea igual.
 */
const ACTOR_TEXTO: Record<ActorTipoAuditoria, string> = {
  PASAJERO: 'Pasajero',
  CONDUCTOR: 'Conductor',
  SISTEMA: 'Sistema',
  OPERADOR: 'Operador',
};

/**
 * Cómo se nombra a quien provocó el evento.
 *
 * 🔴 Un `actorNombre` en `null` tiene DOS causas distintas y la pantalla no las puede confundir:
 * (a) el actor es `SISTEMA` — no hay persona, y decir "sin nombre" sugeriría que falta un dato
 * que nunca existió; (b) hay `actorId` pero el `User` ya no está (cuenta dada de baja o
 * despersonalizada) — ahí el id **sí sirve**, es con lo que se sigue el hilo, así que se muestra
 * crudo en vez de un guion. Mismo criterio que `Incidentes.tsx` con `reportanteId`.
 */
function quien(entrada: EntradaAuditoriaViajeReal): { texto: string; apagado: boolean } {
  if (entrada.actorNombre) return { texto: entrada.actorNombre, apagado: false };
  if (entrada.actorId) return { texto: entrada.actorId, apagado: true };
  return { texto: 'el propio sistema', apagado: true };
}

export function Auditoria() {
  // Paginación por `desplazamiento`, y NO es un adorno: el backend acota la respuesta a 100
  // filas por página incluso si nadie pide paginar. Sin este control, la pantalla mostraría las
  // últimas 100 líneas presentándolas como "la auditoría" — el error hacia el silencio.
  const [pagina, setPagina] = useState(0);
  const [entradas, recargar] = useCargaReal(
    () =>
      consultarAuditoria({
        limite: LIMITE_AUDITORIA,
        desplazamiento: pagina * LIMITE_AUDITORIA,
      }),
    [pagina],
  );
  const [texto, setTexto] = useState('');

  const listos = entradas.estado === 'listo' ? entradas.datos : [];
  const filas = useMemo(() => {
    const buscado = texto.trim().toLowerCase();
    if (buscado === '') return listos;
    return listos.filter(
      (entrada) =>
        (entrada.actorNombre?.toLowerCase().includes(buscado) ?? false) ||
        (entrada.actorId?.toLowerCase().includes(buscado) ?? false) ||
        entrada.accion.toLowerCase().includes(buscado) ||
        entrada.viajeId.toLowerCase().includes(buscado) ||
        (entrada.motivo?.toLowerCase().includes(buscado) ?? false),
    );
  }, [listos, texto]);

  // 🔑 El backend NO devuelve un total: no hay forma de saber cuántas páginas hay. Lo único que
  // se puede afirmar es que si esta página vino LLENA, probablemente haya más — y si vino corta,
  // seguro que no. Se habilita "más antiguas" con ese criterio en vez de inventar un contador.
  const puedeAvanzar = listos.length === LIMITE_AUDITORIA;

  return (
    <Pagina
      rotulo="Solo administrador"
      titulo="Auditoría"
      /*
        🔴 El copy cambió respecto de la versión con datos de ejemplo, y NO por estilo. Decía
        «toda acción que cambia algo deja una línea acá» y contra el backend real eso es FALSO:
        `EventoViaje` es la única bitácora append-only del schema, así que se auditan las
        transiciones de VIAJE y nada más. Suspender un conductor, aprobar un documento o publicar
        un tarifario no dejan rastro histórico. Prometer de más acá es peor que no tener la
        pantalla: alguien buscaría acá una acción que nunca se registró y concluiría que no pasó.
      */
      ayuda="Cada cambio de estado de un viaje: cuándo ocurrió, quién lo provocó y por qué. Sólo viajes — las decisiones sobre conductores, documentos o tarifario todavía no dejan bitácora."
    >
      {!hayBackend ? (
        <div className="bloque">
          <Vacio
            titulo="Sin backend configurado"
            detalle="Falta VITE_API_URL. Esta pantalla lee la bitácora real del servidor, así que sin backend no hay nada que mostrar — y no se inventan datos de ejemplo."
          />
        </div>
      ) : (
        <div className="bloque">
          <div className="barra-filtros">
            <label className="campo">
              <span className="campo__etiqueta rotulo">Buscar</span>
              <input
                className="entrada"
                type="search"
                value={texto}
                onChange={(evento) => setTexto(evento.target.value)}
                placeholder="Persona, transición, id de viaje, motivo…"
              />
            </label>
            <span className="tenue">
              {filas.length} de {listos.length}
            </span>
            <span className="fila" style={{ marginLeft: 'auto', gap: 8 }}>
              <button
                type="button"
                className="boton boton--chico"
                disabled={pagina === 0 || entradas.estado === 'cargando'}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
              >
                Más recientes
              </button>
              <span className="tenue">Página {pagina + 1}</span>
              <button
                type="button"
                className="boton boton--chico"
                disabled={!puedeAvanzar || entradas.estado === 'cargando'}
                onClick={() => setPagina((p) => p + 1)}
              >
                Más antiguas
              </button>
            </span>
          </div>

          {/*
            El buscador filtra SOLO lo que ya está cargado (esta página), no la bitácora entera:
            no hay búsqueda por texto del lado del servidor. Se dice acá y no en un tooltip
            porque un buscador que parece global y es local hace concluir «esto no existe»
            cuando en realidad está dos páginas más atrás.
          */}
          <p className="campo__ayuda" style={{ marginTop: -4 }}>
            La búsqueda filtra sólo las {LIMITE_AUDITORIA} líneas de esta página, no toda la
            bitácora.
          </p>

          {entradas.estado === 'cargando' ? (
            <Cargando filas={5} etiqueta="Cargando la auditoría" />
          ) : entradas.estado === 'error' ? (
            <ConError detalle={entradas.mensaje} alReintentar={recargar} />
          ) : filas.length === 0 ? (
            <Vacio
              titulo={listos.length === 0 ? 'No hay eventos en esta página' : 'Nada coincide'}
              detalle={
                listos.length === 0
                  ? 'Todavía no se registró ninguna transición de viaje, o ya pasaste el final de la bitácora.'
                  : 'Probá con el id del viaje, o con parte del motivo.'
              }
            />
          ) : (
            <Tabla cabeceras={['Cuándo', 'Quién', 'Actor', 'Qué pasó', 'Viaje', 'Por qué']}>
              {filas.map((entrada) => {
                const autor = quien(entrada);
                return (
                  <tr key={entrada.id}>
                    <td className="tenue">{laFechaHora(entrada.ocurridoAt)}</td>
                    <td className={autor.apagado ? 'apagado' : undefined}>{autor.texto}</td>
                    <td className="tenue">{ACTOR_TEXTO[entrada.actorTipo]}</td>
                    {/* `accion` viene ya armada por el backend («BUSCANDO → ASIGNADO»): no se
                        re-arma acá para no tener dos formas de nombrar la misma transición. */}
                    <td>{entrada.accion}</td>
                    <td className="tenue">
                      Viaje
                      <div className="apagado">{entrada.viajeId}</div>
                    </td>
                    <td style={{ fontFamily: 'var(--sans)', maxWidth: 380 }}>
                      {entrada.motivo ?? (
                        <span className="apagado">no se pidió motivo para esta acción</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </Tabla>
          )}
        </div>
      )}

      {/*
        R-07 — las horas de la auditoría son de SERVIDOR (`EventoViaje.ocurridoAt` es
        `@default(now())` de Postgres). Es lo que hace que dos líneas se puedan ordenar entre sí
        aunque las hayan escrito personas con el reloj de su máquina desfasado. Con el backend
        conectado eso ya se cumple de verdad, así que el aviso de «estas fechas son de ejemplo»
        que llevaba la versión mock desapareció junto con los datos de ejemplo.
      */}
      <p className="pagina__ayuda">
        Las horas son de servidor, no del navegador de quien hizo la acción: si no, dos líneas
        escritas por dos personas no se podrían ordenar entre sí. Se muestran en hora de Lima.
      </p>
    </Pagina>
  );
}
